// Ported verbatim from WOOF1_eCRF_v7.html, lines ~1718-2123.

export interface PhysicalSystem {
  key: string;
  label: string;
  matrice: boolean;
  crf: boolean;
}
export const PHYSICAL_SYSTEMS: PhysicalSystem[] = [
  { key: "general", label: "État général", matrice: true, crf: true },
  { key: "cardiovascular", label: "Appareil cardio-vasculaire", matrice: true, crf: true },
  { key: "blood_lymphatic", label: "Sang & lymphatique", matrice: false, crf: true },
  { key: "endocrine", label: "Endocrinien (ex : thyroïde)", matrice: false, crf: true },
  { key: "eye_ent", label: "Yeux, oreilles, nez, gorge", matrice: true, crf: true },
  { key: "oral_cavity", label: "Cavité orale", matrice: true, crf: false },
  { key: "lymph_nodes", label: "Palpation nœuds lymphatiques", matrice: true, crf: false },
  { key: "abdomen", label: "Abdomen / Gastro-intestinal", matrice: true, crf: true },
  { key: "musculoskeletal", label: "Musculo-squelettique", matrice: true, crf: true },
  { key: "urogenital", label: "Uro-génital / Reproductif", matrice: true, crf: true },
  { key: "respiratory", label: "Appareil respiratoire", matrice: true, crf: true },
  { key: "skin", label: "Examen dermatologique", matrice: true, crf: true },
  { key: "neurological", label: "Neurologique", matrice: true, crf: true },
  { key: "psychiatric", label: "Comportement / Psychiatrique", matrice: false, crf: true },
  { key: "renal", label: "Rénal / Urinaire", matrice: false, crf: true },
  { key: "vascular", label: "Vasculaire", matrice: false, crf: true },
  { key: "other", label: "Autres", matrice: true, crf: false },
];

export interface VitalSignDef {
  key: string;
  label: string;
  unit: string;
  type: "number";
  min?: number;
  max?: number;
  step?: number;
}
// Keys follow CDASH VS naming (WEIGHT/TEMP/PULSE/RESP) where a CDASH
// equivalent exists - ecog has no CDASH VS home (commonly modeled as
// QS/ECOG PERFORMANCE STATUS in practice), so it keeps its local name.
export const VITAL_SIGNS: VitalSignDef[] = [
  { key: "WEIGHT", label: "Poids", unit: "kg", type: "number" },
  { key: "TEMP", label: "Température", unit: "°C", type: "number" },
  { key: "PULSE", label: "Fréquence cardiaque", unit: "bpm", type: "number" },
  { key: "RESP", label: "Fréquence respiratoire", unit: "mpm", type: "number" },
  { key: "ecog", label: "Score ECOG modifié", unit: "/5", type: "number", min: 0, max: 5, step: 1 },
  { key: "BCS", label: "Score Corporel", unit: "/5", type: "number", min: 0, max: 5, step: 1 },
];

export interface BloodPanelTest {
  key: string;
  label: string;
  unit: string;
  lln: number;
  uln: number;
  group: "nfs" | "bio";
}
export const BLOOD_PANEL: BloodPanelTest[] = [
  { key: "rbc", label: "Hématies", unit: "10^12/L", lln: 5.1, uln: 7.9, group: "nfs" },
  { key: "hgb", label: "Hémoglobine", unit: "g/dL", lln: 12.4, uln: 19.1, group: "nfs" },
  { key: "hct", label: "Hématocrite", unit: "%", lln: 35, uln: 52, group: "nfs" },
  { key: "mcv", label: "MCV (VGM)", unit: "fL", lln: 60, uln: 71, group: "nfs" },
  { key: "mch", label: "MCH (TGMH)", unit: "pg", lln: 22, uln: 26, group: "nfs" },
  { key: "mchc", label: "MCHC (CGMH)", unit: "g/dL", lln: 34, uln: 38, group: "nfs" },
  { key: "retic", label: "Réticulocytes", unit: "10^9/L", lln: 0, uln: 80, group: "nfs" },
  { key: "wbc", label: "Leucocytes corrigés", unit: "10^9/L", lln: 5.6, uln: 20.4, group: "nfs" },
  { key: "neutro", label: "PN Neutrophiles", unit: "10^9/L", lln: 2.9, uln: 13.6, group: "nfs" },
  { key: "eosino", label: "PN Eosinophiles", unit: "10^9/L", lln: 0, uln: 1.4, group: "nfs" },
  { key: "baso", label: "PN Basophiles", unit: "10^9/L", lln: 0, uln: 0.1, group: "nfs" },
  { key: "lympho", label: "Lymphocytes", unit: "10^9/L", lln: 1.1, uln: 5.3, group: "nfs" },
  { key: "mono", label: "Monocytes", unit: "10^9/L", lln: 0.3, uln: 1.6, group: "nfs" },
  { key: "PLAT", label: "Plaquettes", unit: "10^9/L", lln: 140, uln: 600, group: "nfs" },
  { key: "CREAT", label: "Créatinine", unit: "µmol/L", lln: 60, uln: 125, group: "bio" },
  { key: "UREA", label: "Urée", unit: "mmol/L", lln: 2, uln: 7, group: "bio" },
  { key: "k", label: "K+", unit: "mmol/L", lln: 3.6, uln: 5.6, group: "bio" },
  { key: "na", label: "Na+", unit: "mmol/L", lln: 142, uln: 152, group: "bio" },
  { key: "cl", label: "Cl- (Chlorures)", unit: "mmol/L", lln: 104, uln: 112, group: "bio" },
  { key: "hco3", label: "Bicarbonates", unit: "mmol/L", lln: 18, uln: 26, group: "bio" },
  { key: "BILI", label: "Bilirubine", unit: "µmol/L", lln: 1, uln: 4, group: "bio" },
  { key: "ast", label: "ASAT", unit: "UI/L", lln: 17, uln: 60, group: "bio" },
  { key: "alt", label: "ALAT", unit: "UI/L", lln: 6, uln: 82, group: "bio" },
  { key: "alp", label: "PAL (Phosphatase Alcaline)", unit: "UI/L", lln: 23, uln: 212, group: "bio" },
  { key: "ALB", label: "Albumine", unit: "g/L", lln: 23, uln: 34, group: "bio" },
  { key: "GLUC", label: "Glucose", unit: "mmol/L", lln: 3.5, uln: 6.5, group: "bio" },
];

export const CYTOKINES = [
  { key: "il6", label: "IL-6", unit: "pg/mL" },
  { key: "tnfa", label: "TNFα", unit: "pg/mL" },
  { key: "il10", label: "IL-10", unit: "pg/mL" },
];

export const IRECIST_ORGANS = [
  { code: 1, label: "Adrenal gland" }, { code: 2, label: "Ascites" }, { code: 3, label: "Bone" },
  { code: 4, label: "Bone marrow" }, { code: 5, label: "Brain" }, { code: 6, label: "Breast" },
  { code: 7, label: "Cervix" }, { code: 8, label: "Colon/Large intestine" }, { code: 9, label: "Duodenum" },
  { code: 10, label: "Esophagus/Oesophagus" }, { code: 11, label: "Gall bladder/Bile duct" },
  { code: 12, label: "Head and neck localized lesion" }, { code: 13, label: "Kidney" }, { code: 14, label: "Liver" },
  { code: 15, label: "Lung" }, { code: 16, label: "Lymph nodes" }, { code: 17, label: "Mediastinum" },
  { code: 18, label: "Ovary" }, { code: 19, label: "Pancreas" }, { code: 20, label: "Pelvis (non bone)" },
  { code: 21, label: "Pericardial effusion" }, { code: 22, label: "Peritoneum" }, { code: 23, label: "Pleura" },
  { code: 24, label: "Pleural effusion" }, { code: 25, label: "Prostate" }, { code: 26, label: "Rectum" },
  { code: 27, label: "Salpinx/Fallopian tube" }, { code: 28, label: "Skin" }, { code: 29, label: "Small intestine" },
  { code: 30, label: "Soft tissue" }, { code: 31, label: "Stomach" }, { code: 32, label: "Trachea/Bronchi" },
  { code: 33, label: "Urinary bladder" }, { code: 34, label: "Uterus" }, { code: 35, label: "Other" },
];

export const IRECIST_METHODS = [
  { code: 1, label: "Conventional CT with contrast" }, { code: 2, label: "Conventional CT without contrast" },
  { code: 3, label: "Spiral CT with contrast" }, { code: 4, label: "Spiral CT without contrast" },
  { code: 5, label: "MRI with contrast" }, { code: 6, label: "MRI without contrast" }, { code: 7, label: "X-ray" },
  { code: 8, label: "PET scan" }, { code: 9, label: "Bone scan" }, { code: 10, label: "Ultrasound" },
  { code: 11, label: "Clinical examination" }, { code: 12, label: "Photograph (skin lesions)" },
  { code: 13, label: "Caliper" }, { code: 14, label: "Echography" },
];

export const IRECIST_TIMEPOINTS = [
  { code: "Baseline", label: "Baseline (V1)" },
  { code: "D30", label: "D30 (V4)" },
  { code: "D90", label: "D90 (V6)" },
  { code: "D180", label: "D180 (V9)" },
];

export const IRECIST_RESPONSES = [
  { code: "iCR", label: "iCR — Complete Response" },
  { code: "iPR", label: "iPR — Partial Response" },
  { code: "iSD", label: "iSD — Stable Disease" },
  { code: "iUPD", label: "iUPD — Unconfirmed Progressive Disease" },
  { code: "iCPD", label: "iCPD — Confirmed Progressive Disease" },
  { code: "NE", label: "Not Evaluable" },
];

export interface CorqItem {
  key: string;
  label: string;
  cat: string;
  section: "vitalite_confort" | "bien_etre_affectif" | "mobilite";
  inverse: boolean;
}
export const CORQ_ITEMS: CorqItem[] = [
  { key: "V1", label: "Mon chien manquait d'énergie", cat: "Vitalité / Confort", section: "vitalite_confort", inverse: true },
  { key: "C1", label: "L'appétit de mon chien était diminué", cat: "Vitalité / Confort", section: "vitalite_confort", inverse: true },
  { key: "C2", label: "Mon chien était réticent à se lever", cat: "Vitalité / Confort", section: "vitalite_confort", inverse: true },
  { key: "P1", label: "Mon chien était douloureux ou inconfortable", cat: "Vitalité / Confort", section: "vitalite_confort", inverse: true },
  { key: "V2", label: "Le traitement de mon chien a interféré avec sa qualité de vie", cat: "Vitalité / Confort", section: "vitalite_confort", inverse: true },
  { key: "C3", label: "Mon chien aimait être avec moi / ma famille", cat: "Bien-être affectif", section: "bien_etre_affectif", inverse: false },
  { key: "V3", label: "Mon chien était joueur", cat: "Bien-être affectif", section: "bien_etre_affectif", inverse: false },
  { key: "C4", label: "Mon chien a fait preuve d'un niveau normal d'affection", cat: "Bien-être affectif", section: "bien_etre_affectif", inverse: false },
  { key: "C5", label: "Mon chien aimait être caressé", cat: "Bien-être affectif", section: "bien_etre_affectif", inverse: false },
  { key: "V4", label: "Mon chien a fait ses activités favorites", cat: "Bien-être affectif", section: "bien_etre_affectif", inverse: false },
  { key: "P2", label: "Mon chien a bien dormi la nuit", cat: "Bien-être affectif", section: "bien_etre_affectif", inverse: false },
  { key: "V5", label: "Mon chien s'est comporté comme d'habitude", cat: "Bien-être affectif", section: "bien_etre_affectif", inverse: false },
  { key: "M1", label: "Mon chien avait du mal à se lever ou à se coucher", cat: "Mobilité", section: "mobilite", inverse: true },
  { key: "M2", label: "Mon chien avait du mal à aller se promener", cat: "Mobilité", section: "mobilite", inverse: true },
  { key: "M3", label: "Mon chien est tombé ou a perdu l'équilibre", cat: "Mobilité", section: "mobilite", inverse: true },
  { key: "C6", label: "Mon chien n'a pas mangé sa nourriture habituelle", cat: "Mobilité", section: "mobilite", inverse: true },
  { key: "M4", label: "Mon chien avait du mal à trouver une position confortable", cat: "Mobilité", section: "mobilite", inverse: true },
];

export const CORQ_SECTIONS: Record<string, { label: string; color: string; max_score: number; inverse: boolean }> = {
  vitalite_confort: { label: "Vitalité / Confort", color: "#fef3c7", max_score: 35, inverse: true },
  bien_etre_affectif: { label: "Bien-être affectif", color: "#dbeafe", max_score: 49, inverse: false },
  mobilite: { label: "Mobilité", color: "#dcfce7", max_score: 35, inverse: true },
};

export const EOS_REASONS = [
  { key: "completed_per_protocol", label: "Étude terminée per protocol" },
  { key: "death", label: "Décès" },
  { key: "sae", label: "Événement Indésirable Grave (SAE)" },
  { key: "ae_discontinuation", label: "Événement Indésirable non grave entraînant l'arrêt" },
  { key: "lack_efficacy", label: "Réponse insuffisante / Inefficacité / Progression" },
  { key: "other_medical", label: "Autre événement médical (maladie intercurrente, chirurgie...)" },
  { key: "owner_request", label: "Demande du propriétaire / refus de poursuivre" },
  { key: "poor_compliance", label: "Mauvaise compliance au protocole" },
  { key: "investigator_decision", label: "Décision investigateur (intérêt de l'animal)" },
  { key: "lost_followup", label: "Perdu de vue" },
  { key: "study_terminated", label: "Étude terminée par le sponsor" },
  { key: "screen_failure", label: "Critères d'inclusion/exclusion non satisfaits" },
  { key: "protocol_deviation", label: "Déviation au protocole en sélection" },
  { key: "other", label: "Autre" },
];

export const INCLUSION_CRITERIA = [
  "Mâle ou femelle, toute race, poids ≥ 5 kg",
  "Diagnostic : Mélanome oral OU Carcinome épidermoïde oral (OSCC)",
  "Consentement écrit du propriétaire",
  "Diagnostic cytologique ou histologique de Stade I (>1 cm), II ou III",
  "Chirurgie antérieure acceptable (≥14j avant inclusion) si maladie mesurable persiste",
  "Score ECOG modifié 0 ou 1",
  "Fonctions organiques et médullaires adéquates (créat, urée, Na+, K+, HCO3-, bili, ASAT, ALAT, alb, glu, GR, GB, plq)",
];
export const NON_INCLUSION_CRITERIA = [
  "Maladie métastatique à distance",
  "Chimiothérapie antérieure au-delà de la résection chirurgicale",
  "Patient nécessitant un traitement immunosuppresseur dans les 14j précédant la 1ère dose",
];

export const AE_SERIOUSNESS = ["Not Serious", "Serious"];
export const AE_SAE_CRITERIA = [
  "Décès",
  "Mise en jeu du pronostic vital (Life threatening)",
  "Hospitalisation requise",
  "Prolongation d'hospitalisation existante",
  "Incapacité significative ou persistante (Significant disability)",
  "Anomalie congénitale (Congenital anomaly)",
  "Autre événement médical important (Other important medical condition)",
];
export const AE_CAUSALITY = ["Probable", "Possible", "Unclassified", "Unconclusive", "Unlikely"];
export const AE_OUTCOME = ["Death", "Euthanasia", "Recovered with sequelae", "Recovered without sequelae", "Ongoing"];
export const AE_TYPE = [
  "Adverse reaction", "Lack of efficacy", "Off label use", "Environmental problems",
  "Infectious agent", "Residues", "Other",
];
export const AE_ACTION_TAKEN = [
  "Aucune", "Réduction de dose", "Arrêt temporaire (interruption)", "Arrêt définitif", "Non applicable",
];

export interface MedRoute {
  code: string;
  label: string;
  group: string;
}
export const MED_ROUTES: MedRoute[] = [
  { code: "ORAL", label: "Per os (PO / orale)", group: "Orale / digestive" },
  { code: "BUCCAL", label: "Buccale (muqueuse)", group: "Orale / digestive" },
  { code: "SUBLINGUAL", label: "Sublinguale", group: "Orale / digestive" },
  { code: "DENTAL", label: "Dentaire", group: "Orale / digestive" },
  { code: "ENTERAL", label: "Entérale (sonde)", group: "Orale / digestive" },
  { code: "RECTAL", label: "Rectale", group: "Orale / digestive" },
  { code: "INTRARECTAL", label: "Intra-rectale", group: "Orale / digestive" },
  { code: "INTRAVENOUS", label: "Intraveineuse (IV)", group: "Parentérale" },
  { code: "INTRAVENOUS BOLUS", label: "Intraveineuse bolus", group: "Parentérale" },
  { code: "INTRAVENOUS DRIP", label: "Intraveineuse perfusion", group: "Parentérale" },
  { code: "INTRAMUSCULAR", label: "Intramusculaire (IM)", group: "Parentérale" },
  { code: "SUBCUTANEOUS", label: "Sous-cutanée (SC)", group: "Parentérale" },
  { code: "INTRADERMAL", label: "Intradermique (ID)", group: "Parentérale" },
  { code: "INTRAPERITONEAL", label: "Intra-péritonéale (IP)", group: "Parentérale" },
  { code: "INTRAOSSEOUS", label: "Intra-osseuse", group: "Parentérale" },
  { code: "PARENTERAL", label: "Parentérale (non précisée)", group: "Parentérale" },
  { code: "TOPICAL", label: "Topique (cutanée)", group: "Locale / topique" },
  { code: "TRANSDERMAL", label: "Transdermique (patch)", group: "Locale / topique" },
  { code: "INTRATUMORAL", label: "Intra-tumorale", group: "Locale / topique" },
  { code: "INTRALESIONAL", label: "Intra-lésionnelle", group: "Locale / topique" },
  { code: "INTRAARTICULAR", label: "Intra-articulaire", group: "Locale / topique" },
  { code: "PERINEURAL", label: "Péri-nerveuse", group: "Locale / topique" },
  { code: "INFILTRATION", label: "Infiltration locale", group: "Locale / topique" },
  { code: "EPIDURAL", label: "Épidurale", group: "Locale / topique" },
  { code: "INTRATHECAL", label: "Intra-thécale", group: "Locale / topique" },
  { code: "OPHTHALMIC", label: "Ophtalmique", group: "Muqueuses spécifiques" },
  { code: "INTRAOCULAR", label: "Intra-oculaire", group: "Muqueuses spécifiques" },
  { code: "OTIC", label: "Auriculaire (otique)", group: "Muqueuses spécifiques" },
  { code: "NASAL", label: "Nasale", group: "Muqueuses spécifiques" },
  { code: "VAGINAL", label: "Vaginale", group: "Muqueuses spécifiques" },
  { code: "URETHRAL", label: "Urétrale", group: "Muqueuses spécifiques" },
  { code: "INTRAVESICAL", label: "Intra-vésicale", group: "Muqueuses spécifiques" },
  { code: "INHALATION", label: "Inhalation", group: "Respiratoire" },
  { code: "RESPIRATORY (INHALATION)", label: "Respiratoire (nébulisation)", group: "Respiratoire" },
  { code: "OTHER", label: "Autre (préciser)", group: "Autre" },
  { code: "UNKNOWN", label: "Inconnue", group: "Autre" },
];

export const SCREEN_FAILURE_REASONS = [
  "Critère d'inclusion non rempli",
  "Critère de non-inclusion présent",
  "Décision du propriétaire",
  "Décision de l'investigateur",
  "Tumeur non éligible (taille/localisation)",
  "État général incompatible",
  "Comorbidité incompatible",
  "Bilan biologique incompatible",
  "Perdu de vue avant inclusion",
  "Autre (préciser)",
];

export const PHYSICAL_RESULT_CODES = ["Normal", "Anormal NCS", "Anormal CS", "ND"];
export const LAB_FLAG_CODES = ["Normal", "Low NCS", "Low CS", "High NCS", "High CS"];

export const TUMOR_TYPES = [
  { key: "melanoma", label: "Mélanome oral" },
  { key: "oscc", label: "Carcinome épidermoïde oral (OSCC)" },
];

export const SPECIES_OPTIONS = [
  { key: "animal", label: "Animal" },
  { key: "human", label: "Humain" },
];

// CDISC SEX controlled terminology (M/F/U/UNDIFFERENTIATED). Castration
// status used to be conflated into this field (ME/FS) - it now lives
// separately in REPROD_STATUS_OPTIONS/Patient.reprodStatus.
export const SEX_OPTIONS = [
  { code: "M", label: "Mâle" },
  { code: "F", label: "Femelle" },
  { code: "U", label: "Indéterminé" },
  { code: "UNDIFFERENTIATED", label: "Non différencié" },
];

export const REPROD_STATUS_OPTIONS = [
  { code: "intact", label: "Entier" },
  { code: "neutered", label: "Castré/Stérilisé" },
  { code: "unknown", label: "Inconnu" },
];

export const TNM_T = ["T0", "T1", "T2", "T3"];
export const TNM_N = ["N0", "N1", "N2"];
export const TNM_M = ["M0", "M1"];
export const STAGES = ["I", "II", "III", "IV"];

export const EXAM_CODES = [
  { code: "N", label: "Normal" },
  { code: "ANCS", label: "Anormal NCS (Non Cliniquement Significatif)" },
  { code: "ACS", label: "Anormal CS (Cliniquement Significatif)" },
  { code: "NR", label: "Non Réalisé" },
];
